/**
 * Lazy loader for Agora RTC SDK and extensions.
 * This module provides on-demand loading of Agora SDK to reduce initial bundle size.
 * The SDK is only loaded when getAgoraRTC() is called, typically when entering
 * broadcast, tech setup, or discussion room routes.
 *
 * Why lazy loading? CloudFront only compresses files up to 10MB. The Agora SDK is ~3.6MB,
 * and including it in the main bundle pushed it over 12MB, causing uncompressed transfers.
 * By lazy loading, we keep the main bundle under 10MB and only load Agora when needed.
 */
import { loadDenoiserModule, isDenoiserV2 } from './denoiser/denoiserFactory';
import loggerService from './logger.service';
import {
  safeDiagnosticInfo,
  getCorrelationId,
  createOperationId,
  capDiagnosticString,
  classifyDiagnosticError
} from './diagnostics';

import type { AIDenoiserExtension as AIDenoiserExtensionType } from 'agora-extension-ai-denoiser';
import type VirtualBackgroundExtensionType from 'agora-extension-virtual-background';
import type AgoraRTCType from 'agora-rtc-sdk-ng';

let agoraRTCInstance: typeof AgoraRTCType | null = null;
let denoiserInstance: AIDenoiserExtensionType | null = null;
let virtualBackgroundInstance: InstanceType<typeof VirtualBackgroundExtensionType> | null = null;
let loadingPromise: Promise<typeof AgoraRTCType> | null = null;

/**
 * Lazily loads the Agora RTC SDK and registers extensions.
 * Uses singleton pattern - subsequent calls return the same instance.
 * On load failure, resets state to allow retry on next call.
 */
export async function getAgoraRTC(): Promise<typeof AgoraRTCType> {
  if (agoraRTCInstance) {
    return agoraRTCInstance;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = (async () => {
    try {
      const [{ default: AgoraRTC }, denoiserExtension, { default: VirtualBackgroundExtension }] = await Promise.all([
        import('agora-rtc-sdk-ng'),
        loadDenoiserModule(),
        import('agora-extension-virtual-background')
      ]);

      if (!denoiserInstance) {
        denoiserInstance = denoiserExtension;
        virtualBackgroundInstance = new VirtualBackgroundExtension();
        AgoraRTC.registerExtensions([virtualBackgroundInstance, denoiserInstance]);
        loggerService.info(`[AgoraLoader] Denoiser loaded: ${isDenoiserV2() ? 'v2' : 'v1'}`);
      }

      agoraRTCInstance = AgoraRTC;
      return AgoraRTC;
    } catch (error) {
      loadingPromise = null;
      loggerService.error('[AgoraLoader] Failed to load Agora SDK:', error);
      safeDiagnosticInfo('[AgoraLoader] Failed to load Agora SDK', {
        failureCategory: classifyDiagnosticError(error, 'agora_sdk_load_failed'),
        operation: 'getAgoraRTC',
        correlationId: getCorrelationId(),
        operationId: createOperationId('agora_sdk_load'),
        errorMessage: capDiagnosticString(error instanceof Error ? error.message : String(error)) ?? 'unknown',
        ...(error instanceof Error ? { errorName: capDiagnosticString(error.name) ?? error.name } : {})
      });
      throw error;
    }
  })();

  return loadingPromise;
}

/**
 * Returns the Agora RTC SDK instance synchronously if already loaded.
 * Returns null if the SDK hasn't been loaded yet.
 */
export function getAgoraRTCSync(): typeof AgoraRTCType | null {
  return agoraRTCInstance;
}

/**
 * Returns whether the Agora SDK has been loaded.
 */
export function isAgoraLoaded(): boolean {
  return agoraRTCInstance !== null;
}

/**
 * Returns the AI Denoiser extension instance.
 * Must call getAgoraRTC() first to ensure extensions are loaded.
 */
export function getDenoiserExtension(): AIDenoiserExtensionType | null {
  return denoiserInstance;
}

/**
 * Returns the Virtual Background extension instance.
 * Must call getAgoraRTC() first to ensure extensions are loaded.
 */
export function getVirtualBackgroundExtension(): InstanceType<typeof VirtualBackgroundExtensionType> | null {
  return virtualBackgroundInstance;
}

/**
 * Checks if AI Denoiser is compatible with the current browser.
 * Loads the SDK (and denoiser) if not already loaded.
 */
export async function checkDenoiserCompatibility(): Promise<boolean> {
  try {
    await getAgoraRTC();
    if (denoiserInstance) {
      return denoiserInstance.checkCompatibility();
    }
    return false;
  } catch (error) {
    loggerService.error('[AgoraLoader] Error checking denoiser compatibility:', error);
    safeDiagnosticInfo('[AgoraLoader] Error checking denoiser compatibility', {
      failureCategory: classifyDiagnosticError(error, 'agora_denoiser_compat_check_failed'),
      operation: 'checkDenoiserCompatibility',
      correlationId: getCorrelationId(),
      operationId: createOperationId('denoiser_compat'),
      errorMessage: capDiagnosticString(error instanceof Error ? error.message : String(error)) ?? 'unknown',
      ...(error instanceof Error ? { errorName: capDiagnosticString(error.name) ?? error.name } : {})
    });
    return false;
  }
}

/**
 * AI Denoiser processor level constants.
 * Re-exported from denoiser/constants.ts for backward compatibility.
 */
export { DenoiserLevel } from './denoiser/constants';
export type { DenoiserLevelType } from './denoiser/constants';
